<template>
  <el-drawer
    :visible.sync="drawerVisible"
    direction="rtl"
    :size="drawerSize"
    :with-header="false"
    :wrapper-closable="false"
    append-to-body
    @closed="resetState"
  >
    <div class="drawer-shell">
      <header class="drawer-header">
        <div>
          <div class="drawer-kicker">父任务设置</div>
          <h2>{{ drawerTitle }}</h2>
          <p>设置将一次性应用到该父任务下的全部固定内部模块，并在保存后精确回读。</p>
        </div>
        <el-button
          type="text"
          icon="el-icon-close"
          class="drawer-close"
          @click="drawerVisible = false"
        >
          关闭
        </el-button>
      </header>

      <div class="drawer-body">
        <el-alert
          title="保存不会执行任务、发送通知或启动 Scheduler；Scheduler 已运行时只重载本父任务现有作业。"
          type="info"
          :closable="false"
          show-icon
          class="safety-alert"
        />

        <section class="current-panel" v-loading="optionsLoading">
          <div class="section-heading">
            <div>
              <strong>当前生产配置</strong>
              <p>直接读取后端固定子任务配置，不从页面状态推测。</p>
            </div>
            <el-tag size="mini" type="success" effect="plain">可保存</el-tag>
          </div>

          <el-alert
            v-if="optionsError"
            :title="optionsError"
            type="error"
            :closable="false"
            show-icon
            class="result-alert"
          />

          <el-descriptions
            v-if="optionsReady"
            :column="summaryColumns"
            border
            size="small"
          >
            <el-descriptions-item label="启用状态">{{ currentEnabledText }}</el-descriptions-item>
            <el-descriptions-item label="运行时间">{{ currentScheduleText }}</el-descriptions-item>
            <el-descriptions-item label="监控范围">{{ currentScopeText }}</el-descriptions-item>
            <el-descriptions-item label="通知策略">{{ currentNotificationText }}</el-descriptions-item>
            <el-descriptions-item label="通知渠道">飞书</el-descriptions-item>
            <el-descriptions-item label="父任务版本">{{ shortRevisionText }}</el-descriptions-item>
          </el-descriptions>

          <el-alert
            v-if="legacyFormalScope"
            title="当前个股任务仍使用旧的“第一本账正式区”范围。选择“第一本账全范围”并保存后，正式区和临时区的当前有效股票都会动态纳入。"
            type="warning"
            :closable="false"
            show-icon
            class="migration-alert"
          />
        </section>

        <el-divider content-position="left">编辑设置</el-divider>

        <el-form label-width="102px" size="small" class="settings-form" v-loading="optionsLoading">
          <el-form-item label="父任务">
            <strong>{{ groupName }}</strong>
          </el-form-item>

          <el-form-item label="启用状态">
            <el-radio-group v-model="form.enabled" @change="invalidatePreview">
              <el-radio-button :label="true">启用</el-radio-button>
              <el-radio-button :label="false">停用</el-radio-button>
            </el-radio-group>
          </el-form-item>

          <el-form-item label="运行时间">
            <div class="time-editor">
              <el-time-picker
                v-model="timeToAdd"
                value-format="HH:mm"
                format="HH:mm"
                placeholder="选择时间"
                :clearable="true"
              />
              <el-button type="primary" plain @click="addTime">添加</el-button>
            </div>
            <div v-if="form.scheduleTimes.length" class="time-tags">
              <el-tag
                v-for="item in form.scheduleTimes"
                :key="item"
                closable
                effect="plain"
                @close="removeTime(item)"
              >
                {{ item }}
              </el-tag>
            </div>
            <div v-else class="empty-time-tip">当前未选择运行时间</div>
            <div class="form-tip">最多 12 个时间点；启用父任务时至少保留一个时间。</div>
          </el-form-item>

          <template v-if="isStockGroup">
            <el-form-item label="监控范围">
              <el-radio-group v-model="form.scopeMode" @change="handleScopeModeChange">
                <el-radio label="first_book_all">第一本账全范围</el-radio>
                <el-radio label="custom">自定义范围</el-radio>
              </el-radio-group>
              <div class="form-tip">
                全范围会在每次运行时动态读取第一本账当前有效成员；新增自动纳入，移除自动退出。
              </div>
            </el-form-item>

            <el-form-item v-if="form.scopeMode === 'custom'" label="选择股票">
              <el-select
                v-model="form.scopeSymbols"
                multiple
                filterable
                collapse-tags
                placeholder="从第一本账中选择一只或多只股票"
                class="symbol-select"
                @change="invalidatePreview"
              >
                <el-option
                  v-for="item in firstBookOptions"
                  :key="item.ts_code"
                  :label="symbolOptionLabel(item)"
                  :value="item.ts_code"
                />
              </el-select>
              <div class="form-tip">
                只能选择第一本账当前有效股票，最多 100 只；这里不会新增或删除第一本账成员。
              </div>
            </el-form-item>
          </template>

          <el-form-item label="通知策略">
            <el-radio-group v-model="form.notificationPolicy" class="policy-group" @change="invalidatePreview">
              <el-radio label="summary">每次汇总</el-radio>
              <el-radio label="exception_only">仅异常通知</el-radio>
              <el-radio label="record_only">仅记录，不发送</el-radio>
            </el-radio-group>
            <div class="form-tip">通知渠道固定为飞书；重复过滤始终开启。</div>
          </el-form-item>

          <el-collapse class="advanced-collapse">
            <el-collapse-item name="advanced">
              <template slot="title">
                <div class="collapse-title">
                  <span>高级关键词</span>
                  <el-tag size="mini" type="info" effect="plain">暂未开放</el-tag>
                </div>
              </template>
              <p class="advanced-tip">
                当前生产保存接口不接受关键词字段，避免把历史关键词语义错误迁移到父任务配置。本次只保存启停、时间、个股范围和通知策略。
              </p>
            </el-collapse-item>
          </el-collapse>
        </el-form>

        <div class="primary-actions">
          <el-button
            type="primary"
            plain
            :loading="previewLoading"
            :disabled="!canPreview"
            @click="runPreview"
          >
            预览变更
          </el-button>
          <span class="action-tip">预览有效期 15 分钟；修改任一设置后需要重新预览。</span>
        </div>

        <el-alert
          v-if="requestError"
          :title="requestError"
          type="error"
          :closable="false"
          show-icon
          class="result-alert"
        />

        <template v-if="preview">
          <el-divider content-position="left">保存前确认</el-divider>

          <el-alert
            :title="previewResultTitle"
            :type="previewChangedCount > 0 ? 'success' : 'info'"
            :closable="false"
            show-icon
            class="result-alert"
          />

          <el-alert
            v-if="!previewStillMatches"
            title="设置已在预览后发生变化，请重新预览后再保存。"
            type="warning"
            :closable="false"
            show-icon
            class="result-alert"
          />

          <el-descriptions :column="summaryColumns" border size="small" class="preview-summary">
            <el-descriptions-item label="目标状态">{{ targetEnabledText }}</el-descriptions-item>
            <el-descriptions-item label="目标时间">{{ targetScheduleText }}</el-descriptions-item>
            <el-descriptions-item label="目标范围">{{ targetScopeText }}</el-descriptions-item>
            <el-descriptions-item label="通知策略">{{ notificationPolicyText(previewTarget.notification_policy) }}</el-descriptions-item>
            <el-descriptions-item label="影响内部模块">{{ previewChangedCount }} 个</el-descriptions-item>
            <el-descriptions-item label="预览失效">{{ displayTime(preview.preview_expires_at) }}</el-descriptions-item>
          </el-descriptions>

          <el-table
            v-if="previewRows.length"
            :data="previewRows"
            size="small"
            stripe
            class="task-preview-table"
          >
            <el-table-column label="内部模块" min-width="120">
              <template slot-scope="scope">{{ moduleLabel(scope.row.task_type) }}</template>
            </el-table-column>
            <el-table-column label="变更字段" min-width="170">
              <template slot-scope="scope">{{ changedFieldsText(scope.row) }}</template>
            </el-table-column>
            <el-table-column label="启停" min-width="120">
              <template slot-scope="scope">{{ valueChangeText(scope.row, 'status', statusText) }}</template>
            </el-table-column>
            <el-table-column label="运行时间" min-width="210">
              <template slot-scope="scope">{{ valueChangeText(scope.row, 'schedule') }}</template>
            </el-table-column>
            <el-table-column v-if="isStockGroup" label="范围" min-width="190">
              <template slot-scope="scope">{{ scopeChangeText(scope.row) }}</template>
            </el-table-column>
            <el-table-column label="通知策略" min-width="160">
              <template slot-scope="scope">{{ valueChangeText(scope.row, 'notify_mode', notificationPolicyText) }}</template>
            </el-table-column>
          </el-table>

          <div class="save-panel">
            <div>
              <strong>原子保存</strong>
              <p>一次事务更新全部固定内部模块；任何一项失败都会回滚，不会出现部分保存。</p>
            </div>
            <el-button
              type="primary"
              :loading="applyLoading"
              :disabled="!canApply"
              @click="saveSettings"
            >
              保存设置
            </el-button>
          </div>
        </template>

        <template v-if="applyResult">
          <el-divider content-position="left">保存结果</el-divider>
          <el-alert
            title="父任务设置已保存并完成精确回读。"
            type="success"
            :closable="false"
            show-icon
            class="result-alert"
          />
          <el-descriptions :column="summaryColumns" border size="small">
            <el-descriptions-item label="保存结果编号">{{ applyResult.apply_result_id || '-' }}</el-descriptions-item>
            <el-descriptions-item label="更新内部模块">{{ applyResult.changed_row_count || 0 }} 个</el-descriptions-item>
            <el-descriptions-item label="精确回读">{{ applyExactMatchText }}</el-descriptions-item>
            <el-descriptions-item label="Scheduler">{{ schedulerResultText }}</el-descriptions-item>
          </el-descriptions>
        </template>
      </div>

      <footer class="drawer-footer">
        <el-button @click="drawerVisible = false">关闭</el-button>
        <el-button
          type="primary"
          plain
          :loading="previewLoading"
          :disabled="!canPreview"
          @click="runPreview"
        >
          {{ preview ? '重新预览' : '预览变更' }}
        </el-button>
        <el-button
          v-if="preview"
          type="primary"
          :loading="applyLoading"
          :disabled="!canApply"
          @click="saveSettings"
        >
          保存设置
        </el-button>
      </footer>
    </div>
  </el-drawer>
</template>

<script>
import notificationTaskGroupApi from '@/api/notificationTaskGroup'

const SUPPORTED_GROUP_IDS = [
  'market_event_watch',
  'stock_event_watch',
  'system_health_check'
]

export default {
  name: 'NotificationTaskSettingsPreviewDialog',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    group: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      form: {
        enabled: true,
        scheduleTimes: [],
        scopeMode: '',
        scopeSymbols: [],
        notificationPolicy: 'summary'
      },
      timeToAdd: '',
      optionsLoading: false,
      options: null,
      optionsError: '',
      previewLoading: false,
      applyLoading: false,
      preview: null,
      applyResult: null,
      requestError: '',
      idempotencyKey: '',
      previewFingerprint: ''
    }
  },
  computed: {
    drawerVisible: {
      get() {
        return this.visible
      },
      set(value) {
        this.$emit('update:visible', value)
      }
    },
    drawerSize() {
      if (typeof window !== 'undefined' && window.innerWidth <= 760) return '100%'
      return '780px'
    },
    summaryColumns() {
      if (typeof window !== 'undefined' && window.innerWidth <= 760) return 1
      return 2
    },
    groupId() {
      return this.group && this.group.groupId ? this.group.groupId : ''
    },
    groupName() {
      return this.group && this.group.name ? this.group.name : '通知父任务'
    },
    drawerTitle() {
      return `配置${this.groupName}`
    },
    isStockGroup() {
      return this.groupId === 'stock_event_watch'
    },
    optionsReady() {
      return Boolean(this.options && this.options.ok === true)
    },
    optionTasks() {
      return this.optionsReady && Array.isArray(this.options.tasks) ? this.options.tasks : []
    },
    firstBookOptions() {
      return this.optionsReady && Array.isArray(this.options.first_book_options)
        ? this.options.first_book_options
        : []
    },
    currentEnabledText() {
      if (!this.optionTasks.length) return '暂不可用'
      const active = this.optionTasks.filter(item => item.status === 'active').length
      if (active === this.optionTasks.length) return '已启用'
      if (active === 0) return '已停用'
      return '部分启用'
    },
    currentScheduleText() {
      const times = this.extractTaskTimes(this.optionTasks)
      return times.length ? `每日 ${times.join('、')}` : '尚未配置'
    },
    currentScopeType() {
      const values = [...new Set(this.optionTasks.map(item => item.scope_type).filter(Boolean))]
      return values.length === 1 ? values[0] : ''
    },
    currentScopeText() {
      if (!this.isStockGroup) {
        if (this.groupId === 'market_event_watch') return '系统自动管理范围'
        if (this.groupId === 'system_health_check') return '系统级检查'
        return '暂不可用'
      }
      if (this.currentScopeType === 'first_book_all') return `第一本账全范围（${this.firstBookOptions.length}只）`
      if (this.currentScopeType === 'formal_all') return '第一本账正式区（旧范围）'
      if (this.currentScopeType === 'custom') {
        const symbols = this.currentCustomSymbols
        return `自定义范围（${symbols.length}只）${symbols.length ? `：${symbols.join('、')}` : ''}`
      }
      return '范围配置不一致或暂不可用'
    },
    currentCustomSymbols() {
      if (!this.isStockGroup || this.currentScopeType !== 'custom') return []
      const values = []
      this.optionTasks.forEach(item => {
        String(item.scope_value || '')
          .split(',')
          .map(value => value.trim().toUpperCase())
          .filter(Boolean)
          .forEach(value => values.push(value))
      })
      return [...new Set(values)].sort()
    },
    currentNotificationPolicy() {
      const values = [...new Set(this.optionTasks.map(item => item.notify_mode || 'summary'))]
      return values.length === 1 ? values[0] : ''
    },
    currentNotificationText() {
      if (!this.currentNotificationPolicy) return '内部模块配置不一致'
      return this.notificationPolicyText(this.currentNotificationPolicy)
    },
    shortRevisionText() {
      const value = this.options && this.options.current_group_revision
      if (!value) return '-'
      return value.length > 28 ? `${value.slice(0, 28)}…` : value
    },
    legacyFormalScope() {
      return this.isStockGroup && this.currentScopeType === 'formal_all'
    },
    canPreview() {
      if (!SUPPORTED_GROUP_IDS.includes(this.groupId) || !this.optionsReady) return false
      if (this.form.enabled && !this.form.scheduleTimes.length) return false
      if (this.isStockGroup) {
        if (!['first_book_all', 'custom'].includes(this.form.scopeMode)) return false
        if (this.form.scopeMode === 'custom' && !this.form.scopeSymbols.length) return false
      }
      return true
    },
    intentFingerprint() {
      return JSON.stringify(this.buildIntent(false))
    },
    previewStillMatches() {
      return Boolean(this.preview && this.previewFingerprint && this.previewFingerprint === this.intentFingerprint)
    },
    previewRows() {
      return this.preview && Array.isArray(this.preview.row_plans) ? this.preview.row_plans : []
    },
    previewTarget() {
      return (this.preview && this.preview.target) || {}
    },
    previewChangedCount() {
      return Number((this.preview && this.preview.changed_row_count) || 0)
    },
    canApply() {
      return Boolean(
        this.preview &&
        this.preview.ok === true &&
        this.previewChangedCount > 0 &&
        this.previewStillMatches &&
        !this.isPreviewExpired
      )
    },
    isPreviewExpired() {
      const value = this.preview && this.preview.preview_expires_at
      if (!value) return true
      const time = new Date(value).getTime()
      return !Number.isFinite(time) || Date.now() > time
    },
    previewResultTitle() {
      if (!this.preview) return ''
      if (this.previewChangedCount === 0) return '当前配置与目标配置一致，无需保存。'
      return `将同时更新 ${this.previewChangedCount} 个固定内部模块。`
    },
    targetEnabledText() {
      return this.previewTarget.enabled === false ? '停用' : '启用'
    },
    targetScheduleText() {
      const times = Array.isArray(this.previewTarget.schedule_times) ? this.previewTarget.schedule_times : []
      return times.length ? `每日 ${times.join('、')}` : '无运行时间'
    },
    targetScopeText() {
      if (!this.isStockGroup) return this.currentScopeText
      if (this.previewTarget.scope_mode === 'first_book_all') {
        const count = Array.isArray(this.previewTarget.scope_symbols) ? this.previewTarget.scope_symbols.length : 0
        return `第一本账全范围（${count}只）`
      }
      if (this.previewTarget.scope_mode === 'custom') {
        const values = Array.isArray(this.previewTarget.scope_symbols) ? this.previewTarget.scope_symbols : []
        return `自定义范围（${values.length}只）：${values.join('、')}`
      }
      return '暂不可用'
    },
    applyExactMatchText() {
      return this.applyResult && this.applyResult.readback && this.applyResult.readback.exact_match === true
        ? '通过'
        : '未确认'
    },
    schedulerResultText() {
      const value = (this.applyResult && this.applyResult.scheduler) || {}
      if (value.scheduler_reconciled === true) return '已重载现有作业'
      if (value.scheduler_running === false) return '当前未运行，未启动'
      return value.reason || '未变更'
    }
  },
  watch: {
    visible(value) {
      if (value) this.initialize()
    },
    group() {
      if (this.visible) this.initialize()
    }
  },
  methods: {
    async initialize() {
      this.resetState(false)
      await this.loadOptions()
    },
    resetState(clearOptions = true) {
      this.timeToAdd = ''
      this.previewLoading = false
      this.applyLoading = false
      this.preview = null
      this.applyResult = null
      this.requestError = ''
      this.idempotencyKey = ''
      this.previewFingerprint = ''
      if (clearOptions) {
        this.optionsLoading = false
        this.options = null
        this.optionsError = ''
      }
    },
    async loadOptions() {
      const requestedGroupId = this.groupId
      if (!requestedGroupId) return
      this.optionsLoading = true
      this.optionsError = ''
      try {
        const payload = await notificationTaskGroupApi.getSettingsOptions(requestedGroupId)
        if (this.groupId !== requestedGroupId) return
        this.options = payload || null
        this.initializeFormFromOptions()
      } catch (error) {
        if (this.groupId === requestedGroupId) {
          this.options = null
          this.optionsError = this.errorText(error, '加载父任务生产设置失败。')
        }
      } finally {
        if (this.groupId === requestedGroupId) this.optionsLoading = false
      }
    },
    initializeFormFromOptions() {
      const tasks = this.optionTasks
      const enabled = tasks.length
        ? tasks.every(item => item.status === 'active')
        : !(this.group && this.group.enabled === false)
      const scheduleTimes = this.extractTaskTimes(tasks)
      const notificationPolicy = this.currentNotificationPolicy || 'summary'
      let scopeMode = ''
      let scopeSymbols = []
      if (this.isStockGroup) {
        if (this.currentScopeType === 'custom') {
          scopeMode = 'custom'
          scopeSymbols = this.currentCustomSymbols
        } else {
          scopeMode = 'first_book_all'
        }
      }
      this.form = {
        enabled,
        scheduleTimes,
        scopeMode,
        scopeSymbols,
        notificationPolicy
      }
    },
    extractTaskTimes(tasks) {
      const values = []
      ;(Array.isArray(tasks) ? tasks : []).forEach(item => {
        String(item.schedule || '')
          .split(',')
          .map(value => value.trim())
          .filter(value => /^([01]\d|2[0-3]):[0-5]\d$/.test(value))
          .forEach(value => values.push(value))
      })
      return this.normalizeTimes(values)
    },
    normalizeTimes(items) {
      return [...new Set(
        (Array.isArray(items) ? items : [])
          .map(item => String(item || '').trim())
          .filter(item => /^([01]\d|2[0-3]):[0-5]\d$/.test(item))
      )].sort()
    },
    addTime() {
      if (!this.timeToAdd) return
      const values = this.normalizeTimes([...this.form.scheduleTimes, this.timeToAdd])
      if (values.length > 12) {
        this.$message.warning('最多只能设置 12 个运行时间')
        return
      }
      this.form.scheduleTimes = values
      this.timeToAdd = ''
      this.invalidatePreview()
    },
    removeTime(item) {
      this.form.scheduleTimes = this.form.scheduleTimes.filter(value => value !== item)
      this.invalidatePreview()
    },
    handleScopeModeChange() {
      if (this.form.scopeMode === 'first_book_all') this.form.scopeSymbols = []
      this.invalidatePreview()
    },
    invalidatePreview() {
      this.applyResult = null
      this.requestError = ''
    },
    makeIdempotencyKey() {
      const randomPart = Math.random().toString(36).slice(2, 10)
      return `frontend-production-settings-${this.groupId}-${Date.now()}-${randomPart}`
    },
    buildIntent(includeIdempotency = true) {
      const result = {
        schedule_times: this.normalizeTimes(this.form.scheduleTimes),
        enabled: Boolean(this.form.enabled),
        notification_policy: this.form.notificationPolicy || 'summary',
        scope_mode: this.isStockGroup ? this.form.scopeMode : null,
        scope_symbols: this.isStockGroup && this.form.scopeMode === 'custom'
          ? [...new Set(this.form.scopeSymbols.map(item => String(item).toUpperCase()))].sort()
          : []
      }
      if (includeIdempotency) result.idempotency_key = this.idempotencyKey
      return result
    },
    async runPreview() {
      if (!this.canPreview) return
      this.previewLoading = true
      this.preview = null
      this.applyResult = null
      this.requestError = ''
      this.idempotencyKey = this.makeIdempotencyKey()
      try {
        const payload = await notificationTaskGroupApi.previewProductionSettings(
          this.groupId,
          this.buildIntent(true)
        )
        this.preview = payload || null
        this.previewFingerprint = this.intentFingerprint
      } catch (error) {
        this.requestError = this.errorText(error, '设置预览失败，请检查输入后重试。')
      } finally {
        this.previewLoading = false
      }
    },
    async saveSettings() {
      if (!this.canApply) return
      try {
        await this.$confirm(
          `将一次性保存“${this.groupName}”下全部固定内部模块的设置。任何一项失败都会回滚，是否继续？`,
          '确认保存父任务设置',
          {
            confirmButtonText: '确认保存',
            cancelButtonText: '取消',
            type: 'warning'
          }
        )
      } catch (error) {
        return
      }

      this.applyLoading = true
      this.requestError = ''
      try {
        const payload = await notificationTaskGroupApi.applySettings(this.groupId, {
          ...this.buildIntent(true),
          preview_id: this.preview.preview_id,
          preview_digest: this.preview.preview_digest,
          preview_created_at: this.preview.preview_created_at,
          preview_expires_at: this.preview.preview_expires_at,
          before_group_revision: this.preview.before_group_revision,
          confirm_apply: true
        })
        this.applyResult = payload || null
        this.$message.success('父任务设置已保存')
        this.$emit('saved', {
          groupId: this.groupId,
          result: this.applyResult
        })
        if (this.applyResult && this.applyResult.readback && Array.isArray(this.applyResult.readback.tasks)) {
          this.options = {
            ...(this.options || {}),
            ok: true,
            current_group_revision: this.applyResult.after_group_revision,
            tasks: this.applyResult.readback.tasks
          }
          this.initializeFormFromOptions()
        }
        this.preview = null
        this.previewFingerprint = ''
      } catch (error) {
        this.requestError = this.errorText(error, '保存设置失败，未确认任何部分变更。')
      } finally {
        this.applyLoading = false
      }
    },
    errorText(error, fallback) {
      const response = error && error.response
      const detail = response && response.data ? response.data.detail : null
      const code = detail && typeof detail === 'object' ? detail.code : ''
      const message = detail && typeof detail === 'object' ? detail.message : detail
      const map = {
        GROUP_NOT_CONFIGURABLE: '该父任务尚未开放生产设置。',
        PARENT_CHILD_SET_MISMATCH: '父任务内部模块集合不完整或存在重复，已阻止保存。',
        ACTIVE_GROUP_REQUIRES_SCHEDULE: '启用父任务时至少需要一个运行时间。',
        STOCK_SCOPE_MODE_REQUIRED: '请选择第一本账全范围或自定义范围。',
        CUSTOM_SCOPE_REQUIRES_SYMBOL: '自定义范围至少需要选择一只第一本账股票。',
        CUSTOM_SCOPE_OUTSIDE_FIRST_BOOK: '自定义范围包含不在第一本账当前有效成员中的股票。',
        PREVIEW_EXPIRED: '设置预览已过期，请重新预览。',
        PREVIEW_ID_MISMATCH: '设置已变化，请重新预览。',
        PREVIEW_DIGEST_MISMATCH: '设置预览校验不一致，请重新预览。',
        GROUP_REVISION_MISMATCH: '后台配置在预览后发生变化，请刷新并重新预览。',
        POST_COMMIT_READBACK_MISMATCH: '保存后的精确回读不一致，系统已报告失败。'
      }
      return map[code] || (typeof message === 'string' && message ? message : fallback)
    },
    symbolOptionLabel(item) {
      const zone = item.zone === 'formal' ? '正式区' : (item.zone === 'temp' ? '临时区' : item.zone)
      return `${item.name || '-'}（${item.ts_code}） · ${zone || '未分类'}`
    },
    notificationPolicyText(value) {
      const map = {
        summary: '每次汇总',
        exception_only: '仅异常通知',
        record_only: '仅记录，不发送',
        detail: '详情通知'
      }
      return map[value] || value || '-'
    },
    moduleLabel(taskType) {
      const map = {
        announcement_watch: '公告',
        news_watch: '市场新闻',
        stock_news_watch: '个股新闻',
        irm_qa_watch: '董秘问答',
        system_health_check: '系统自检'
      }
      return map[taskType] || taskType || '未知模块'
    },
    statusText(value) {
      if (value === 'active') return '启用'
      if (value === 'paused') return '停用'
      return value || '-'
    },
    changedFieldsText(row) {
      const before = (row && row.before) || {}
      const after = (row && row.after) || {}
      const labels = {
        status: '启停',
        schedule: '运行时间',
        scope_type: '范围模式',
        scope_value: '范围股票',
        notify_channels: '通知渠道',
        notify_mode: '通知策略',
        dedupe_enabled: '重复过滤'
      }
      const fields = Object.keys(labels).filter(key => JSON.stringify(before[key]) !== JSON.stringify(after[key]))
      return fields.length ? fields.map(key => labels[key]).join('、') : '无变化'
    },
    valueChangeText(row, field, formatter) {
      const before = row && row.before ? row.before[field] : null
      const after = row && row.after ? row.after[field] : null
      const format = typeof formatter === 'function' ? formatter : (value => value || '无')
      if (JSON.stringify(before) === JSON.stringify(after)) return format(after)
      return `${format(before)} → ${format(after)}`
    },
    scopeValueText(type, value) {
      if (type === 'first_book_all') return '第一本账全范围'
      if (type === 'formal_all') return '第一本账正式区'
      if (type === 'custom') {
        const values = String(value || '').split(',').filter(Boolean)
        return `自定义（${values.length}只）`
      }
      return type || '-'
    },
    scopeChangeText(row) {
      const before = (row && row.before) || {}
      const after = (row && row.after) || {}
      const beforeText = this.scopeValueText(before.scope_type, before.scope_value)
      const afterText = this.scopeValueText(after.scope_type, after.scope_value)
      return beforeText === afterText ? afterText : `${beforeText} → ${afterText}`
    },
    displayTime(value) {
      if (!value) return '-'
      return String(value).replace('T', ' ').slice(0, 19)
    }
  }
}
</script>

<style scoped>
.drawer-shell {
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #fff;
}
.drawer-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 18px;
  padding: 22px 24px 18px;
  border-bottom: 1px solid #e8edf4;
  background: #f8fafc;
}
.drawer-kicker {
  color: #2563eb;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.04em;
}
.drawer-header h2 {
  margin: 7px 0 6px;
  color: #0f172a;
  font-size: 22px;
  line-height: 1.35;
}
.drawer-header p {
  margin: 0;
  color: #64748b;
  font-size: 13px;
  line-height: 1.6;
}
.drawer-close {
  flex: 0 0 auto;
  color: #64748b;
}
.drawer-body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 20px 24px 28px;
}
.drawer-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 14px 24px;
  border-top: 1px solid #e8edf4;
  background: #fff;
}
.safety-alert,
.result-alert,
.migration-alert {
  margin-bottom: 14px;
}
.current-panel {
  margin-top: 16px;
  padding: 16px;
  border: 1px solid #e5eaf2;
  border-radius: 12px;
  background: #fbfcfe;
}
.section-heading {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
}
.section-heading strong {
  color: #0f172a;
  font-size: 15px;
}
.section-heading p {
  margin: 5px 0 0;
  color: #7c8a9d;
  font-size: 12px;
  line-height: 1.6;
}
.settings-form {
  margin-top: 18px;
}
.time-editor {
  display: flex;
  gap: 10px;
  align-items: center;
}
.time-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
}
.empty-time-tip {
  margin-top: 10px;
  color: #94a3b8;
  font-size: 12px;
}
.form-tip,
.action-tip,
.advanced-tip {
  color: #94a3b8;
  font-size: 12px;
  line-height: 1.65;
}
.form-tip {
  margin-top: 8px;
}
.symbol-select {
  width: 100%;
}
.policy-group {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}
.advanced-collapse {
  margin: 4px 0 18px 102px;
  border-top: 0;
  border-bottom: 0;
}
.collapse-title {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding-right: 8px;
  color: #334155;
  font-weight: 600;
}
.advanced-tip {
  margin: 0 4px 10px;
}
.primary-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 6px;
}
.preview-summary,
.task-preview-table {
  margin-bottom: 16px;
}
.save-panel {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  padding: 16px;
  border: 1px solid #bfdbfe;
  border-radius: 10px;
  background: #eff6ff;
}
.save-panel strong {
  color: #1e3a8a;
}
.save-panel p {
  margin: 5px 0 0;
  color: #64748b;
  font-size: 12px;
  line-height: 1.6;
}
@media (max-width: 760px) {
  .drawer-header,
  .drawer-body,
  .drawer-footer {
    padding-left: 16px;
    padding-right: 16px;
  }
  .drawer-header h2 {
    font-size: 20px;
  }
  .section-heading,
  .save-panel,
  .primary-actions {
    align-items: flex-start;
    flex-direction: column;
  }
  .time-editor {
    align-items: stretch;
    flex-direction: column;
  }
  .time-editor .el-date-editor.el-input,
  .time-editor .el-date-editor.el-input__inner {
    width: 100%;
  }
  .advanced-collapse {
    margin-left: 0;
  }
}
</style>
